import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";

interface DocumentModalState {
  open: boolean;
  blobUrl: string | null;
  contentType: string;
  title: string;
  loading: boolean;
  error: string | null;
}

interface DocumentModalProps {
  modal: DocumentModalState;
  onClose: () => void;
}

export default function DocumentModal({ modal, onClose }: DocumentModalProps) {
  const [imgZoom, setImgZoom] = useState(100);

  const handlePrint = () => {
    if (!modal.blobUrl) return;
    const w = window.open(modal.blobUrl, "_blank");
    if (w) {
      w.onload = () => {
        w.print();
      };
    }
  };

  const extForContentType = (ct: string): string => {
    if (ct.startsWith("image/")) return "jpg";
    if (ct.includes("pdf")) return "pdf";
    if (ct.includes("xml")) return "xml";
    return "bin";
  };

  const handleDownload = () => {
    if (!modal.blobUrl) return;
    const a = document.createElement("a");
    a.href = modal.blobUrl;
    a.download = `${modal.title}.${extForContentType(modal.contentType)}`;
    a.click();
  };

  return (
    <Dialog open={modal.open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl w-[min(90vw,56rem)] h-[min(85vh,48rem)] flex flex-col p-0">
        <DialogHeader className="px-6 pt-5 pb-3 border-b shrink-0">
          <div className="flex items-center justify-between gap-4">
            <DialogTitle className="text-base">{modal.title}</DialogTitle>
            {modal.blobUrl && (
              <div className="flex items-center gap-1">
                {modal.contentType.startsWith("image/") && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setImgZoom((z) => Math.max(25, z - 25))}
                    >
                      <SrIcon name="zoom-out" className="h-4 w-4"  />
                    </Button>
                    <span className="text-xs text-muted-foreground w-10 text-center tabular-nums">{imgZoom}%</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setImgZoom((z) => Math.min(300, z + 25))}
                    >
                      <SrIcon name="zoom-in" className="h-4 w-4"  />
                    </Button>
                  </>
                )}
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handlePrint} title="Skriv ut">
                  <SrIcon name="printer" className="h-4 w-4"  />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleDownload} title="Last ned">
                  <SrIcon name="download" className="h-4 w-4"  />
                </Button>
              </div>
            )}
          </div>
        </DialogHeader>
        <div className="flex-1 min-h-0 px-1 pb-1">
          {modal.loading && (
            <div className="flex items-center justify-center h-full gap-2 text-muted-foreground">
              <SrIcon name="loader2" className="w-5 h-5 animate-spin"  />
              <span className="text-sm">Henter dokument fra PowerOffice...</span>
            </div>
          )}
          {modal.error && (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm text-destructive">{modal.error}</p>
            </div>
          )}
          {modal.blobUrl &&
            (modal.contentType.startsWith("image/") ? (
              <div className="flex items-center justify-center h-full p-4 overflow-auto">
                <img
                  src={modal.blobUrl}
                  alt={modal.title}
                  style={{ width: `${imgZoom}%`, height: "auto" }}
                  className="object-contain transition-all duration-150"
                />
              </div>
            ) : modal.contentType.includes("pdf") ? (
              <iframe
                src={`${modal.blobUrl}#view=FitH`}
                className="w-full h-full rounded-b-lg"
                title="Bilagsdokument"
              />
            ) : (
              // VIB-874: ikke-PDF/ikke-image (f.eks. EHF/XML for ubokførte
              // bilag). Tving det ikke inn i en PDF-iframe — det gir bare
              // «Kunne ikke laste inn PDF». Vis melding + nedlasting i stedet.
              <div className="flex flex-col items-center justify-center h-full gap-3 text-center p-6">
                <SrIcon name="file" className="w-8 h-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground max-w-sm">
                  Ingen visuell forhåndsvisning tilgjengelig for dette bilaget
                  (kun EHF/XML, f.eks. for ubokførte bilag). Last ned for å se
                  innholdet.
                </p>
                <Button size="sm" onClick={handleDownload}>
                  <SrIcon name="download" className="w-4 h-4 mr-1" /> Last ned
                </Button>
              </div>
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
